import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, TextField, Autocomplete } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import BookingFormHeader from './bookingFormHeader';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseServiceBookingFormContext } from '../utils/contexts/nurseServiceBookingFormContext';
import { ConstantValues, FormMode, Districts } from '../utils/extensions/constantEx'
import { GetDayJSValueFromUTCDate, GetDateFromDayJSValue, NurseServiceBookingAPIUrl, GetServiceTypesAPIUrl } from '../utils/extensions/functionEx';
import PageLoading from '../shared/components/backdrop';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const ServiceBookingForm = (props) => {
    const { formMode, selectedBookingId, selectedNurseId, selectedNurseName } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { selectedBookingJosn, bookingFormValue, patientAddress,
        SetSelectedBookingJson, SetBookingFormValue, SetBookingContactAddress } = useContext(NurseServiceBookingFormContext);

    const navigate = useNavigate();
    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    const [isOpenPageLoad, setIsOpenPageLoad] = useState(false);
    const [formOpenMode, setFormOpenMode] = useState(formMode ?? FormMode.NewMode);
    const [cleared, setCleared] = useState(false);
    const [serviceTypes, setServiceTypes] = useState([]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    useEffect(() => {

        async function fetchServiceTypes() {
            try {
                const response = await fetch(GetServiceTypesAPIUrl())
                if (response.status === 200) {
                    setServiceTypes(await response.json());
                } else {
                    ShowAPIError(response)
                    setServiceTypes([]);
                }
            }
            catch (error) {
                OpenErrorAlert(error);
                setServiceTypes([]);
            }
        }
        setIsOpenPageLoad(true);
        fetchServiceTypes();
        if (selectedBookingId && selectedBookingId > 0) {
            setFormMode(FormMode.ViewMode);
            getServiceBooking(selectedBookingId);
        } else {
            setFormMode(formMode ?? FormMode.NewMode);
        }
        setIsOpenPageLoad(false);
    }, [selectedBookingId, formMode, selectedNurseId, selectedNurseName]);

    useEffect(() => {
        if (selectedBookingJosn && selectedBookingJosn.service_type_id && selectedBookingJosn.service_type_id > 0 && serviceTypes && serviceTypes.length > 0) {
            const selectedDutyType = serviceTypes.find(col => col.id === selectedBookingJosn.service_type_id);

            SetBookingFormValue({
                ...bookingFormValue,
                selectedServiceType: selectedDutyType
            });
        }
    }, [selectedBookingJosn, serviceTypes]);

    const setFormMode = (tempFrmMode) => {
        setFormOpenMode(tempFrmMode);
        clearForm();
    }

    const clearForm = () => {
        const tempFormFormInitalState = {
            id: 0,
            nurseId: 0,
            nurseName: selectedNurseName ?? '',
            bookingDate: null,
            selectedServiceType: null,
            serviceDutyDays: 0,
            bookedDate: null,
            patientName: '',
            patientPhone: '',
            patientPlace: '',
            notes: ''
        };
        SetBookingFormValue({ ...tempFormFormInitalState });
        SetBookingContactAddress({
            id: ConstantValues.DefaultContactAddressId,
            addressText: '',
            district: '',
            pincode: ''
        });
        SetSelectedBookingJson(null);
    }

    const handleEditClick = () => {
        setFormOpenMode(FormMode.EditMode);
    }

    const handleSaveClick = async () => {
        try {
            let tempBookingId = 0;
            if (!bookingFormValue.bookingDate || !bookingFormValue.selectedServiceType || bookingFormValue.serviceDutyDays <= 0
                || !bookingFormValue.bookedDate || bookingFormValue.patientName.trim().length === 0
                || bookingFormValue.patientPhone.trim().length === 0
            ) {
                return;
            }
            setIsOpenPageLoad(true);

            if (formOpenMode === FormMode.NewMode) {
                tempBookingId = await addNewBookingAPI();
            } else if (formOpenMode === FormMode.EditMode) {
                tempBookingId = await updateBookingAPI();
            }

            if (tempBookingId > 0) {
                clearForm();
                setFormMode(FormMode.ViewMode);
                await getServiceBooking(tempBookingId);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
        finally {
            handlePageLoadingClose();
        }
    }

    const addNewBookingAPI = async () => {
        try {
            let tempAddressId = patientAddress.id;
            if (tempAddressId === ConstantValues.DefaultContactAddressId && (patientAddress.addressText.trim().length > 0
                || patientAddress.district.trim().length > 0 || patientAddress.pincode.trim().length > 0)) {
                tempAddressId = 0;
            }

            const tempBookingJson = {
                id: 0,
                nurse_id: selectedNurseId ?? 0,
                booking_date: GetDateFromDayJSValue(bookingFormValue.bookingDate),
                service_type_id: bookingFormValue.selectedServiceType.id,
                duty_days: bookingFormValue.serviceDutyDays,
                booked_date: GetDateFromDayJSValue(bookingFormValue.bookedDate),
                patient_name: bookingFormValue.patientName.trim(),
                place: bookingFormValue.patientPlace.trim(),
                phone_no: bookingFormValue.patientPhone.trim(),
                notes: bookingFormValue.notes?.trim() ?? '',
                address_id: tempAddressId,
                address_text: patientAddress.addressText.trim(),
                district: patientAddress.district.trim(),
                pincode: patientAddress.pincode.trim()
            };
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseServiceBookingAPIUrl(homeState.loggedUser.companyCode), {
                method: "POST",
                headers: headers,
                body: JSON.stringify(tempBookingJson)
            })
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 201) {
                OpenSuccessAlert('New service booking added successfully.');

                const tempJsonNurse = await response.json();
                return tempJsonNurse.id;
            }
            ShowAPIError(response);
            return -1;
        }
        catch (error) {
            OpenErrorAlert(error)
            return -1;
        }
    }

    const updateBookingAPI = async () => {
        try {
            let tempAddressId = patientAddress.id;
            if (tempAddressId === ConstantValues.DefaultContactAddressId && (patientAddress.addressText.trim().length > 0
                || patientAddress.district.trim().length > 0 || patientAddress.pincode.trim().length > 0)) {
                tempAddressId = 0;
            }

            const tempBookingJson = {
                id: bookingFormValue.id,
                nurse_id: selectedNurseId ?? 0,
                booking_date: GetDateFromDayJSValue(bookingFormValue.bookingDate),
                service_type_id: bookingFormValue.selectedServiceType.id,
                duty_days: bookingFormValue.serviceDutyDays,
                booked_date: GetDateFromDayJSValue(bookingFormValue.bookedDate),
                patient_name: bookingFormValue.patientName.trim(),
                place: bookingFormValue.patientPlace.trim(),
                phone_no: bookingFormValue.patientPhone.trim(),
                notes: bookingFormValue.notes?.trim() ?? '',
                address_id: tempAddressId,
                address_text: patientAddress.addressText.trim(),
                district: patientAddress.district.trim(),
                pincode: patientAddress.pincode.trim()
            };
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseServiceBookingAPIUrl(homeState.loggedUser.companyCode), {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(tempBookingJson)
            })
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('service booking updated successfully.');

                const tempJsonNurse = await response.json();
                return tempJsonNurse.id;
            }
            ShowAPIError(response);
            return -1;
        }
        catch (error) {
            OpenErrorAlert(error)
            return -1;
        }
    }

    const handleCancelClick = async () => {
        if (formOpenMode === FormMode.ViewMode) {
            navigate("/nurse-service-booking", {
                replace: false
            });
        }
        else if (formOpenMode === FormMode.EditMode) {
            setFormOpenMode(FormMode.ViewMode);
            clearForm();
            await getServiceBooking(selectedBookingId);
        } else if (formOpenMode === FormMode.NewMode) {
            clearForm();
            setFormOpenMode(FormMode.NewMode);
        }
    }

    const handleDeleteClick = () => {
        if (formOpenMode === FormMode.ViewMode) { }
    }

    const handlePageLoadingClose = () => {
        setIsOpenPageLoad(false);
    }

    const getServiceBooking = async (bookingId) => {
        const headers = new Headers();
        headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
        const response = await fetch(NurseServiceBookingAPIUrl(homeState.loggedUser.companyCode, bookingId), { headers: headers });

        if (response.status === 401) {
            OpenErrorAlert('User login history expired, please log in again.');
            setCookie('hplus_home_care_user_session_code', '', { path: '/' });
            ClearLoggedUser();
        } else if (response.status === 200) {
            const dbBookingJson = await response.json();

            SetBookingFormValue({
                id: dbBookingJson.id,
                nurseId: dbBookingJson.nurse_id,
                bookingDate: GetDayJSValueFromUTCDate(dbBookingJson.booking_date),
                selectedServiceTypeId: dbBookingJson.service_type_id,
                serviceDutyDays: dbBookingJson.duty_days,
                bookedDate: GetDayJSValueFromUTCDate(dbBookingJson.booked_date),
                patientName: dbBookingJson.patient_name,
                patientPlace: dbBookingJson.place,
                patientPhone: dbBookingJson.phone_no,
                notes: dbBookingJson.notes,
                nurseName: dbBookingJson.nurse_name,
                selectedServiceType: null
            });

            SetBookingContactAddress({
                id: dbBookingJson.address_id ?? ConstantValues.DefaultContactAddressId,
                addressText: dbBookingJson.address_text ?? '',
                district: dbBookingJson.district ?? '',
                pincode: dbBookingJson.pincode ?? ''
            });
            SetSelectedBookingJson(dbBookingJson);
        } else {
            ShowAPIError(response);
        }
    }


    return (
        <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>

                <Stack spacing={0}>
                    {
                        isOpenPageLoad &&
                        <PageLoading isOpenPageLoading={isOpenPageLoad} handleClose={handlePageLoadingClose} />
                    }
                    <BookingFormHeader formMode={formOpenMode} handleEditClick={handleEditClick} handleSaveClick={handleSaveClick}
                        handleCancelClick={handleCancelClick} handleDeleteClick={handleDeleteClick} />
                    <Box sx={{ padding: "16px", maxHeight: '80vh', overflowY: 'auto' }}>
                        <Stack>
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid size={2}></Grid>
                                    <Grid size={8}>
                                        <Grid container spacing={2}>
                                            <Grid size={3}>
                                                <DatePicker format="DD/MM/YYYY"
                                                    label="Booking Date" readOnly={formOpenMode === FormMode.ViewMode}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small", fullWidth: true, required: true,
                                                            error: !bookingFormValue.bookingDate ? true : false,
                                                            helperText: !bookingFormValue.bookingDate ? 'Enter booking date' : ''
                                                        },
                                                        field: {
                                                            clearable: true,
                                                            onClear: () => setCleared(true)
                                                        }
                                                    }}
                                                    value={bookingFormValue.bookingDate} minDate={dayjs().add(-3, 'day')} maxDate={dayjs()}
                                                    onChange={(newValue) => {
                                                        SetBookingFormValue({ ...bookingFormValue, bookingDate: newValue });
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <Autocomplete readOnly={formOpenMode === FormMode.ViewMode}
                                                    id="combo-box-service-type"
                                                    options={serviceTypes} value={bookingFormValue.selectedServiceType}
                                                    getOptionLabel={(option) => option.service_type_name}
                                                    onChange={(event, newValue) => {
                                                        const tempFormValue = { ...bookingFormValue };
                                                        tempFormValue.selectedServiceType = newValue;
                                                        SetBookingFormValue(tempFormValue);
                                                    }}
                                                    renderInput={(params) =>
                                                        <TextField {...params} label="Duty" size="small" fullWidth required
                                                            error={!bookingFormValue.selectedServiceType ? true : false}
                                                            helperText={!bookingFormValue.selectedServiceType ? 'Select duty' : ''}
                                                        />}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <TextField size="small" fullWidth required type='number'
                                                    label="Duty Days"
                                                    error={bookingFormValue.serviceDutyDays <= 0 ? true : false}
                                                    helperText={bookingFormValue.serviceDutyDays <= 0 ? 'Enter duty days' : ''}
                                                    value={bookingFormValue.serviceDutyDays}
                                                    onChange={(event) => {
                                                        const tempFormValue = { ...bookingFormValue };
                                                        tempFormValue.serviceDutyDays = event.target.value ?? 0;
                                                        SetBookingFormValue(tempFormValue);
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <DatePicker format="DD/MM/YYYY"
                                                    label="Booked Date" readOnly={formOpenMode === FormMode.ViewMode}
                                                    slotProps={{
                                                        textField: {
                                                            size: "small", fullWidth: true, required: true,
                                                            error: !bookingFormValue.bookedDate ? true : false,
                                                            helperText: !bookingFormValue.bookedDate ? 'Enter booked date' : ''
                                                        },
                                                        field: {
                                                            clearable: true,
                                                            onClear: () => setCleared(true)
                                                        }
                                                    }}
                                                    value={bookingFormValue.bookedDate} minDate={dayjs().add(1, 'day')} maxDate={dayjs().add(365, 'day')}
                                                    onChange={(newValue) => {
                                                        SetBookingFormValue({ ...bookingFormValue, bookedDate: newValue });
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={9}>
                                                <TextField size="small" fullWidth
                                                    label="Nurse Name"
                                                    value={bookingFormValue.nurseName}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: true
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <TextField size="small" fullWidth
                                                    label="Patient Name"
                                                    value={bookingFormValue.patientName}
                                                    onChange={(event) => SetBookingFormValue({ ...bookingFormValue, patientName: event.target.value })}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                    error={bookingFormValue.patientName.trim().length === 0 ? true : false}
                                                    helperText={bookingFormValue.patientName.trim().length === 0 ? 'Enter valid patient name' : ''}
                                                />
                                            </Grid>
                                            <Grid size={2}>
                                                <TextField size="small" fullWidth
                                                    label="Patient Phone"
                                                    value={bookingFormValue.patientPhone}
                                                    onChange={(event) => SetBookingFormValue({ ...bookingFormValue, patientPhone: event.target.value })}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                    error={bookingFormValue.patientPhone.trim().length === 0 ? true : false}
                                                    helperText={bookingFormValue.patientPhone.trim().length === 0 ? 'Enter valid patient phone' : ''}
                                                />
                                            </Grid>
                                            <Grid size={4}>
                                                <TextField size="small" fullWidth
                                                    label="Patient Place"
                                                    value={bookingFormValue.patientPlace}
                                                    onChange={(event) => SetBookingFormValue({ ...bookingFormValue, patientPlace: event.target.value })}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={6}>
                                                <Grid container spacing={1}>
                                                    <Grid size={12}>
                                                        <TextField size="small" fullWidth
                                                            label="Patient Address"
                                                            multiline
                                                            rows={4}
                                                            value={patientAddress.addressText}
                                                            onChange={(event) => {
                                                                SetBookingContactAddress({ ...patientAddress, addressText: event.target.value });
                                                            }}
                                                            slotProps={{
                                                                input: {
                                                                    readOnly: formOpenMode === FormMode.ViewMode
                                                                },
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid size={8}>
                                                        <Autocomplete
                                                            readOnly={formOpenMode === FormMode.ViewMode}
                                                            id="combo-box-district"
                                                            options={Districts}
                                                            value={patientAddress.district}
                                                            getOptionLabel={(option) => option}
                                                            onChange={(event, newValue) => {
                                                                SetBookingContactAddress({ ...patientAddress, district: newValue });
                                                            }}
                                                            renderInput={(params) =>
                                                                <TextField {...params} label="District" size="small" fullWidth
                                                                />}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        <TextField size="small" fullWidth label="PIN Code"
                                                            value={patientAddress.pincode}
                                                            onChange={(event) => {
                                                                SetBookingContactAddress({ ...patientAddress, pincode: event.target.value });
                                                            }}
                                                            slotProps={{
                                                                input: {
                                                                    readOnly: formOpenMode === FormMode.ViewMode
                                                                },
                                                            }}
                                                        />
                                                    </Grid>

                                                </Grid>
                                            </Grid>
                                            <Grid size={6}>
                                                <TextField size="small" fullWidth
                                                    label="Notes"
                                                    multiline
                                                    rows={6}
                                                    value={bookingFormValue.notes}
                                                    onChange={(event) => {
                                                        SetBookingFormValue({ ...bookingFormValue, notes: event.target.value });
                                                    }}
                                                    slotProps={{
                                                        input: {
                                                            readOnly: formOpenMode === FormMode.ViewMode
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={2}></Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            </LocalizationProvider>
        </Box>
    );
}

export default ServiceBookingForm;
